
  (() => {
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const number0 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
    const number1 = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const euro0 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

    const init = (scope = document) => {
      scope.querySelectorAll('[data-mp-yield-calculator]').forEach((root) => {
        if (root.dataset.mpYieldReady === 'true') return;
        root.dataset.mpYieldReady = 'true';

        const field = (name) => root.querySelector(`[data-field="${name}"]`);
        const write = (name, value) => {
          root.querySelectorAll(`[data-output="${name}"]`).forEach((node) => {
            node.textContent = value;
          });
        };
        const sectionRoot = root.closest('[data-mp-yield-section]');
        const currency = sectionRoot?.dataset.mpCurrency || 'EUR';
        const packageMoney = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency,
        });
        let packageState = {
          priceCents: null,
          title: '',
          options: [],
          modulePower: Number(field('module').value),
          moduleCount: null,
          moduleWatts: null,
          inverterPower: null,
          storage: Number(field('storage').value),
        };
        let consumptionMode = 'people';

        const derivePackage = (detail) => {
          const options = Array.isArray(detail?.variant?.options)
            ? detail.variant.options.filter(Boolean)
            : Array.isArray(detail?.options) ? detail.options.filter(Boolean) : [];
          const title = detail?.title || root.querySelector('[data-output="package-title"]')?.textContent || '';
          const source = [options.join(' | '), title, detail?.media?.alt].filter(Boolean).join(' | ');
          const moduleMatch = source.match(/(\d{3,4})\s*w?\s*[x×]\s*(\d{1,2})/i)
            || source.match(/(\d{1,2})\s*[x×]\s*(\d{3,4})\s*w/i);
          let moduleWatts = null;
          let moduleCount = null;
          if (moduleMatch) {
            const first = Number(moduleMatch[1]);
            const second = Number(moduleMatch[2]);
            moduleWatts = first > second ? first : second;
            moduleCount = first > second ? second : first;
          }
          const totalPowerMatch = source.match(/(\d{4,5})\s*(?:watt(?:\s*peak)?|wp)\b/i);
          const modulePower = moduleWatts && moduleCount
            ? moduleWatts * moduleCount
            : Number(totalPowerMatch?.[1]) || packageState.modulePower;
          if (!moduleCount && modulePower) {
            moduleWatts = modulePower % 500 === 0 ? 500 : modulePower % 400 === 0 ? 400 : null;
            moduleCount = moduleWatts ? Math.round(modulePower / moduleWatts) : null;
          }
          const inverterPower = Number(
            source.match(/(?:bifazial\s*\+|wechselrichter[^0-9]*)(\d{3,4})\s*w/i)?.[1]
            || source.match(/\+\s*(\d{3,4})\s*w\b/i)?.[1]
          ) || packageState.inverterPower;

          const explicitStorage = source.match(/(\d+(?:[.,]\d+)?)\s*kwh/i)?.[1];
          const abCount = Number(source.match(/(\d+)\s*x\s*ab\s*3000/i)?.[1] || 0);
          let storage = explicitStorage ? Number(explicitStorage.replace(',', '.')) : null;
          if (storage == null && /solarflow\s*2400/i.test(source)) storage = 2.4 + abCount * 2.88;
          if (storage == null && /ohne\s+(?:erweiterungs)?batterie|ohne\s+speicher/i.test(source)) storage = 0;

          return {
            priceCents: Number(detail?.variant?.price) || packageState.priceCents,
            title,
            options,
            modulePower,
            moduleCount,
            moduleWatts,
            inverterPower,
            storage: storage == null ? packageState.storage : storage,
          };
        };

        const applyPackage = (detail) => {
          packageState = derivePackage(detail);
          const moduleInput = field('module');
          const inverterInput = field('inverter');
          const storageInput = field('storage');
          const batteryInput = field('battery');
          if (moduleInput && Number.isFinite(packageState.modulePower)) {
            moduleInput.value = String(clamp(packageState.modulePower, Number(moduleInput.min), Number(moduleInput.max)));
            moduleInput.disabled = true;
          }
          if (inverterInput && Number.isFinite(packageState.inverterPower)) {
            inverterInput.value = String(clamp(packageState.inverterPower, Number(inverterInput.min), Number(inverterInput.max)));
            inverterInput.disabled = true;
          }
          if (storageInput && Number.isFinite(packageState.storage)) {
            const max = Math.max(Number(storageInput.max), Math.ceil(packageState.storage));
            storageInput.max = String(max);
            storageInput.value = String(clamp(packageState.storage, Number(storageInput.min), max));
            storageInput.disabled = true;
          }
          if (batteryInput) {
            batteryInput.checked = Number(packageState.storage) > 0;
            batteryInput.disabled = true;
          }
          write('package-title', packageState.title);
          write('package-configuration', packageState.options.join(' · '));
          write(
            'package-modules',
            packageState.moduleCount && packageState.moduleWatts
              ? `${number0.format(packageState.moduleCount)} × ${number0.format(packageState.moduleWatts)} W (${number0.format(packageState.modulePower)} Wp)`
              : `${number0.format(packageState.modulePower)} Wp`
          );
          write(
            'package-storage',
            Number(packageState.storage) > 0 ? `${number1.format(packageState.storage)} kWh` : 'ohne Speicher'
          );

          const image = root.querySelector('[data-mp-yield-recommendation-media] img');
          if (image && detail?.media?.src) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = detail.media.src;
            image.alt = detail.media.alt || packageState.title;
          }
        };

        const update = () => {
          const modulePower = Number(field('module').value);
          const inverterPower = Number(field('inverter').value);
          const regionalHours = Number(field('region').value);
          const orientationFactor = Number(field('orientation').value);
          const tilt = Number(field('tilt').value);
          const electricityPrice = Number(field('price').value);
          const people = Number(field('people').value);
          const enteredKwh = Number(field('kwh').value);
          const hasBattery = field('battery').checked;
          const storage = Number(field('storage').value);
          const annualConsumption = consumptionMode === 'people' ? 1500 + Math.max(0, people - 1) * 700 : enteredKwh;

          const effectiveKwp = Math.min(modulePower / 1000, inverterPower / 1000 * 1.6);
          const tiltFactor = clamp(1 - Math.abs(tilt - 35) * 0.00215, 0.78, 1);
          const annualYield = Math.max(1, Math.round(effectiveKwp * regionalHours * orientationFactor * tiltFactor * 0.928));
          const demandRatio = annualConsumption / annualYield;
          const withoutBatteryRate = clamp(0.35 + demandRatio * 0.05, 0.35, 0.56);
          const selfRate = hasBattery
            ? clamp(0.56 + storage * 0.045 + demandRatio * 0.08, 0.56, 0.9)
            : withoutBatteryRate;
          const selfUsed = Math.round(annualYield * selfRate);
          const firstYearSavings = selfUsed * electricityPrice;
          const co2 = Math.round(annualYield * 0.22);
          const modeledInvestment = Math.round(
            modulePower / 1000 * 250
            + inverterPower * 0.3
            + (hasBattery ? storage * 200 + 465 : 280)
          );
          const investment = packageState.priceCents != null
            ? packageState.priceCents / 100
            : modeledInvestment;

          let twentySavings = 0;
          let twentyYield = 0;
          for (let year = 0; year < 20; year += 1) {
            const yearlyYield = annualYield * Math.pow(0.995, year);
            twentyYield += yearlyYield;
            twentySavings += yearlyYield * selfRate * electricityPrice * Math.pow(1.05, year);
          }

          const averageSavings = twentySavings / 20;
          const storageValue = hasBattery ? Math.max(0, (selfRate - withoutBatteryRate) * annualYield * electricityPrice) : 0;
          const payback = firstYearSavings > 0 ? investment / firstYearSavings : 0;
          const roiFirst = investment > 0 ? firstYearSavings / investment * 100 : 0;
          const roiAverage = investment > 0 ? averageSavings / investment * 100 : 0;

          write('module-label', `${number0.format(modulePower)} Wp`);
          write('inverter-label', `${number0.format(inverterPower)} W`);
          write('tilt-label', `${number0.format(tilt)}°`);
          write('price-label', `${number1.format(electricityPrice)} €/kWh`);
          write('people-label', `${number0.format(people)} ${people === 1 ? 'Person' : 'Personen'}`);
          write('kwh-label', `${number0.format(enteredKwh)} kWh`);
          write('storage-label', hasBattery ? `${number0.format(storage)} kWh` : 'ohne Speicher');
          write('annual-yield', `${number0.format(annualYield)} kWh/Jahr`);
          write('self-rate', `${number0.format(selfRate * 100)} %`);
          write('self-used', `${number0.format(selfUsed)} kWh/Jahr`);
          write('co2', `${number0.format(co2)} kg/Jahr`);
          write('co2-short', `${number0.format(co2)} kg`);
          write('first-year', euro0.format(firstYearSavings));
          write('average-savings', euro0.format(averageSavings));
          write('payback', `ca. ${number1.format(payback)} Jahre`);
          write('twenty-savings', euro0.format(twentySavings));
          write('twenty-yield', `${number0.format(twentyYield)} kWh`);
          write('storage-value', `${euro0.format(storageValue)}/Jahr`);
          write('investment', packageMoney.format(investment));
          write('roi-first', `${number1.format(roiFirst)} %`);
          write('roi-average', `${number1.format(roiAverage)} %`);
          write('monthly-savings', euro0.format(averageSavings / 12));

          field('storage').disabled = true;
          root.querySelector('[data-storage-control]')?.classList.toggle('is-disabled', !hasBattery);
        };

        root.querySelectorAll('[data-consumption-mode]').forEach((button) => {
          button.addEventListener('click', () => {
            consumptionMode = button.dataset.consumptionMode;
            root.querySelectorAll('[data-consumption-mode]').forEach((candidate) => {
              const active = candidate === button;
              candidate.classList.toggle('is-active', active);
              candidate.setAttribute('aria-pressed', String(active));
            });
            root.querySelectorAll('[data-consumption-panel]').forEach((panel) => {
              panel.hidden = panel.dataset.consumptionPanel !== consumptionMode;
            });
            update();
          });
        });

        root.querySelectorAll('input, select').forEach((control) => {
          control.addEventListener('input', update);
          control.addEventListener('change', update);
        });
        root.querySelector('[data-yield-refresh]')?.addEventListener('click', update);
        const initialDataElement = sectionRoot?.querySelector('[data-mp-yield-initial-variant]');
        if (initialDataElement) {
          try {
            applyPackage(JSON.parse(initialDataElement.textContent));
          } catch (error) {
            // Keep the conservative calculator defaults if product data cannot be read.
          }
        }
        document.addEventListener('mp:product-variant-change', (event) => {
          if (String(event.detail?.productId || '') !== String(sectionRoot?.dataset.mpProductId || '')) return;
          applyPackage({
            ...event.detail,
            title: packageState.title,
          });
          update();
        });
        update();
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => init(), { once: true });
    } else {
      init();
    }
    document.addEventListener('shopify:section:load', (event) => init(event.target));
  })();
